import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import DetailPageHeader from '../detail_page_header/DetailPageHeader';
import queryString from 'querystring';
import { updateNavbarState } from '../../actions/navbar_actions';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/sessionHooks';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { Button } from 'antd';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import {
	archiveGroupedSiteSurveys,
	getSSLocationsByTitleAndTemplate,
	unarchiveGroupedSiteSurveys,
} from '../../thunks/site_surveys_grouped_thunks';

const getArchiveStateFromRecord = (record) => {
	if (!record) {
		return undefined;
	}
	if (Object.prototype.hasOwnProperty.call(record, 'isArchived')) {
		return record.isArchived;
	}
	if (Object.prototype.hasOwnProperty.call(record, 'archived')) {
		return record.archived;
	}
	if (record.archivedAt) {
		return true;
	}
	if (record.siteSurvey) {
		return getArchiveStateFromRecord(record.siteSurvey);
	}
	if (record.siteSurveyGroup) {
		return getArchiveStateFromRecord(record.siteSurveyGroup);
	}
	return undefined;
};

const SiteSurveysWalkthroughbyTitle = ({
	companyConfig,
	updateNavbar,
	childRoutes,
	siteSurveysGrouped,
	siteSurveysLocationAndWalkthrough,
}) => {
	const location = useLocation();
	const history = useHistory();
	const dispatch = useDispatch();
	const { title: siteSurveyTitle, templateId: siteSurveyTemplateId } = useParams<{
		title: string;
		templateId: string;
	}>();
	const { userType } = useCurrentUser();

	const parsedQueryParams = useMemo(() => {
		let searchString = location.search;
		if (searchString[0] === '?') {
			searchString = searchString.slice(1);
		}
		return queryString.parse(searchString);
	}, [location.search]);

	// Capture the group archive state from the initial URL query params (set by the overview page).
	// This must not change when the child's ReduxDataTable syncs its tab-specific isArchived filter
	// back into the URL via handleQueryParams — that reflects the tab, not the group's archive state.
	const archiveStateFromQueryRef = React.useRef(parsedQueryParams.isArchived === 'true');
	const archiveStateFromQuery = archiveStateFromQueryRef.current;

	const archiveStateFromGroupedRecord = useMemo(() => {
		const groupedRecordKey = `${siteSurveyTitle}-${siteSurveyTemplateId}`;
		const groupedRecords = nullSafeGetOrElse('records', siteSurveysGrouped, {});
		return getArchiveStateFromRecord(groupedRecords[groupedRecordKey]);
	}, [siteSurveyTemplateId, siteSurveyTitle, siteSurveysGrouped]);

	const archiveStateFromLocations = useMemo(() => {
		const locationRecords = nullSafeGetOrElse('records', siteSurveysLocationAndWalkthrough, {});
		const matchingLocationRecord = Object.keys(locationRecords)
			.map((key) => locationRecords[key])
			.find((record) => {
				const recordTemplateId =
					record && record.siteSurveyTemplateId !== undefined
						? record.siteSurveyTemplateId
						: record && record.siteSurvey
						? record.siteSurvey.siteSurveyTemplateId
						: record && record.siteSurveyGroup
						? record.siteSurveyGroup.templateId
						: undefined;
				const recordTitle =
					record && record.title
						? record.title
						: record && record.siteSurvey
						? record.siteSurvey.title
						: record && record.siteSurveyGroup
						? record.siteSurveyGroup.title
						: undefined;

				return (
					`${recordTemplateId}` === `${siteSurveyTemplateId}` && recordTitle === siteSurveyTitle
				);
			});
		return getArchiveStateFromRecord(matchingLocationRecord);
	}, [siteSurveyTemplateId, siteSurveyTitle, siteSurveysLocationAndWalkthrough]);

	const isArchived = useMemo(() => {
		if (archiveStateFromGroupedRecord !== undefined) {
			return archiveStateFromGroupedRecord;
		}
		return archiveStateFromQuery;
	}, [archiveStateFromGroupedRecord, archiveStateFromQuery]);

	const overviewTab = isArchived ? 'archived' : 'all';

	useEffect(() => {
		if (archiveStateFromGroupedRecord === undefined && archiveStateFromLocations === undefined) {
			dispatch(
				getSSLocationsByTitleAndTemplate(userType)(siteSurveyTitle, siteSurveyTemplateId)
			).catch(() => null);
		}
	}, [
		archiveStateFromGroupedRecord,
		archiveStateFromLocations,
		dispatch,
		siteSurveyTemplateId,
		siteSurveyTitle,
		userType,
	]);

	useEffect(() => {
		if (parsedQueryParams.backlinkParams) {
			updateNavbar(
				'Back To Site Surveys',
				`/${userType}/siteSurveys/overview/${overviewTab}?${parsedQueryParams.backlinkParams}`
			);
		}
	}, [parsedQueryParams, updateNavbar, userType, overviewTab]);

	const handleTabChange = useCallback(
		(key) => {
			history.push(
				`/${userType}/siteSurveys/byTitle/${siteSurveyTitle}/template/${siteSurveyTemplateId}/${key}`
			);
		},
		[history, siteSurveyTemplateId, siteSurveyTitle, userType]
	);

	const checkTabActive = useCallback(
		(key) => {
			const pathname = history.location.pathname;
			return pathname.indexOf('/' + key) !== -1;
		},
		[history.location.pathname]
	);

	const tabs = [
		{
			key: 'locations',
			name: 'Active',
		},
		{
			key: 'archivedLocations',
			name: 'Archived',
		},
	];

	const onEdit = useCallback(() => {
		history.push(
			getLinkWIthBackLinkParams(
				location,
				'Back to Site Survey',
				`/${userType}/siteSurveys/editByTitle/${siteSurveyTitle}/template/${siteSurveyTemplateId}`
			)
		);
	}, [history, location, siteSurveyTemplateId, siteSurveyTitle, userType]);

	const [archiveLoading, setArchiveLoading] = useState(false);

	const handleArchive = useCallback(() => {
		setArchiveLoading(true);
		dispatch(archiveGroupedSiteSurveys(userType)(siteSurveyTitle, siteSurveyTemplateId, false))
			.then(() => {
				history.push(`/${userType}/siteSurveys/overview/archived`);
			})
			.catch(() => null)
			.finally(() => setArchiveLoading(false));
	}, [dispatch, history, siteSurveyTemplateId, siteSurveyTitle, userType]);

	const handleUnarchive = useCallback(() => {
		setArchiveLoading(true);
		dispatch(unarchiveGroupedSiteSurveys(userType)(siteSurveyTitle, siteSurveyTemplateId, false))
			.then(() => {
				history.push(`/${userType}/siteSurveys/overview/all`);
			})
			.catch(() => null)
			.finally(() => setArchiveLoading(false));
	}, [dispatch, history, siteSurveyTemplateId, siteSurveyTitle, userType]);

	return (
		<DetailPageHeader
			exactPath={`/${userType}/siteSurveys/byTitle/${siteSurveyTitle}/template/${siteSurveyTemplateId}`}
			redirectPath={`/${userType}/siteSurveys/byTitle/${siteSurveyTitle}/template/${siteSurveyTemplateId}/locations`}
			backLinkText={'Back to site surveys'}
			backLinkTo={`/${userType}/siteSurveys/overview/${overviewTab}`}
			title={decodeURIComponent(siteSurveyTitle)}
			actions={
				<div className="flex items-center gap-4">
					<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_SITE_SURVEYS}>
						<Button ghost={true} onClick={onEdit} className="uppercase">
							Edit
						</Button>
					</AccessPermissionChecker>
					<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_SITE_SURVEYS}>
						{isArchived ? (
							<Button
								ghost={true}
								loading={archiveLoading}
								onClick={handleUnarchive}
								className="uppercase"
							>
								Unarchive
							</Button>
						) : (
							<Button
								ghost={true}
								loading={archiveLoading}
								onClick={handleArchive}
								className="uppercase"
							>
								Archive
							</Button>
						)}
					</AccessPermissionChecker>
				</div>
			}
			tabs={tabs}
			backgroundColor={nullSafeGet('detail.config.theme.navblock', companyConfig)}
			handleTabChange={handleTabChange}
			checkTabActive={checkTabActive}
			childRoutes={childRoutes}
		/>
	);
};

const mapStateToProps = (state) => ({
	companyConfig: state.company_config,
	siteSurveysGrouped: state['site_surveys/grouped'],
	siteSurveysLocationAndWalkthrough: state['site_surveys/locationandwalkthrough'],
});

const mapDispatchToProps = (dispatch) => ({
	updateNavbar: (backlinkTitle, backlinkTo, navbarBackgroundColor) =>
		dispatch(updateNavbarState(backlinkTitle, backlinkTo, navbarBackgroundColor)),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(SiteSurveysWalkthroughbyTitle)
);
